// Get API base URL - use relative /api on custom hostnames to avoid mixed content (HTTP on HTTPS page)
export const getApiUrl = () => {
    const hostname = window.location.hostname;
    const defaultDomain = process.env.REACT_APP_DEFAULT_DOMAIN || 'localhost';
    const isCustomHostname = hostname !== 'localhost' &&
        !hostname.includes('127.0.0.1') &&
        !hostname.includes('0.0.0.0') &&
        hostname !== defaultDomain &&
        (defaultDomain === 'localhost' || !hostname.endsWith('.' + defaultDomain)) &&
        hostname.includes('.');
    if (isCustomHostname) return '/api';
    return process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
};

// API Configuration
const API_CONFIG = {
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 10000,
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    debugMode: process.env.REACT_APP_DEBUG_MODE === 'true'
};

// API Helper class
class ApiClient {
    constructor() {
        this.baseURL = API_CONFIG.baseURL;
        this.timeout = API_CONFIG.timeout;
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        };
    }

    // Get authentication token from localStorage
    getAuthToken() {
        const token = localStorage.getItem('accessToken');
        if (API_CONFIG.debugMode && token) {
            console.log('API Client: Token found, length:', token.length);
        } else if (API_CONFIG.debugMode && !token) {
            console.warn('API Client: No token found in localStorage');
        }
        return token;
    }

    // Create request options
    createRequestOptions(method, body = null, customHeaders = {}, customTimeout = null) {
        const headers = { ...this.defaultHeaders, ...customHeaders };
        
        // Add authentication token if available
        const token = this.getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
            if (API_CONFIG.debugMode) {
                console.log('API Client: Added Authorization header');
            }
        } else {
            if (API_CONFIG.debugMode) {
                console.warn('API Client: No token available, request will be unauthenticated');
            }
        }

        // Use custom timeout if provided, otherwise use default
        const timeout = customTimeout !== null ? customTimeout : this.timeout;

        const options = {
            method,
            headers,
            signal: AbortSignal.timeout(timeout)
        };

        if (body && method !== 'GET') {
            if (body instanceof FormData) {
                // Remove Content-Type header for FormData (browser sets it with boundary)
                delete options.headers['Content-Type'];
                options.body = body;
            } else {
                options.body = JSON.stringify(body);
            }
        }

        return options;
    }

    // Build full URL - use getApiUrl() for custom hostname support (avoids mixed content)
    buildURL(endpoint) {
        const baseURL = getApiUrl();
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        const cleanBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        return `${cleanBaseURL}/${cleanEndpoint}`;
    }

    // Generic request method
    async request(endpoint, options = {}, customTimeout = null) {
        const url = this.buildURL(endpoint);

        // Override timeout if custom timeout is provided
        if (customTimeout !== null && options.signal) {
            options.signal = AbortSignal.timeout(customTimeout);
        }

        if (API_CONFIG.debugMode) {
            console.log(`API Request: ${options.method || 'GET'} ${url}`, options.body);
        }

        try {
            const response = await fetch(url, options);

            if (API_CONFIG.debugMode) {
                console.log(`API Response: ${response.status} ${response.statusText}`);
            }

            // Handle non-JSON responses (like file downloads)
            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            
            // Clone response for error handling (response body can only be read once)
            const responseClone = response.clone();
            
            // If response is not OK, try to get error details
            if (!response.ok) {
                if (isJson) {
                    try {
                        const data = await response.json();
                        // Include details if available for better error messages
                        const errorMessage = data.details 
                            ? `${data.error || 'Request failed'}: ${data.details}`
                            : data.error || `HTTP ${response.status}: ${response.statusText}`;
                        throw new Error(errorMessage);
                    } catch (parseError) {
                        // If JSON parsing fails, it might be an HTML error page or malformed JSON
                        if (parseError instanceof SyntaxError) {
                            try {
                                const text = await responseClone.text();
                                // If it looks like HTML, provide a more helpful error
                                if (text.trim().startsWith('<')) {
                                    throw new Error(`HTTP ${response.status}: Server returned an HTML error page. Check server logs for details.`);
                                }
                                throw new Error(`HTTP ${response.status}: Server returned invalid JSON: ${text.substring(0, 100)}`);
                            } catch (textError) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                        }
                        throw parseError;
                    }
                } else {
                    // Non-JSON error response
                    const text = await response.text();
                    throw new Error(`HTTP ${response.status}: ${response.statusText}${text ? ` - ${text.substring(0, 200)}` : ''}`);
                }
            }
            
            // Success response - parse as JSON if applicable
            if (isJson) {
                try {
                    const data = await response.json();
                    return data;
                } catch (parseError) {
                    if (parseError instanceof SyntaxError) {
                        throw new Error(`Server returned invalid JSON response`);
                    }
                    throw parseError;
                }
            } else {
                return response;
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                throw new Error('Request timeout - please try again');
            }

            console.error(`API Error for ${url}:`, error);
            throw error;
        }
    }

    // HTTP method helpers
    async get(endpoint, headers = {}, timeout = null) {
        return this.request(endpoint, this.createRequestOptions('GET', null, headers, timeout), timeout);
    }

    async post(endpoint, body, headers = {}, timeout = null) {
        return this.request(endpoint, this.createRequestOptions('POST', body, headers, timeout), timeout);
    }

    async put(endpoint, body, headers = {}, timeout = null) {
        return this.request(endpoint, this.createRequestOptions('PUT', body, headers, timeout), timeout);
    }

    async delete(endpoint, body = null, headers = {}, timeout = null) {
        return this.request(endpoint, this.createRequestOptions('DELETE', body, headers, timeout), timeout);
    }

    // File upload helper
    async uploadFile(endpoint, file, additionalData = {}) {
        const formData = new FormData();
        formData.append('file', file);

        Object.keys(additionalData).forEach(key => {
            formData.append(key, additionalData[key]);
        });

        return this.post(endpoint, formData);
    }

    // Health check
    async healthCheck() {
        try {
            return await this.get('/health');
        } catch (error) {
            return { status: 'unhealthy', error: error.message };
        }
    }
}

// Create and export API client instance
export const apiClient = new ApiClient();

// Export configuration for use in components
export { API_CONFIG };

// Helper function to check if API is available
export const checkApiHealth = async () => {
    try {
        const health = await apiClient.healthCheck();
        return health.status === 'healthy';
    } catch {
        return false;
    }
};

export default apiClient;